import { getConfig } from "@/config";
import { messages } from "@/shared/i18n";
import { ERROR_CODE_MESSAGES } from "@/shared/lib/error-code-messages";

function getErrorMessageByStatus(status: number): string {
  const { errors } = messages;
  switch (status) {
    case 400:
      return errors.validation;
    case 401:
      return errors.unauthorized;
    case 403:
      return errors.forbidden;
    case 404:
      return errors.notFound;
    case 409:
      return errors.conflict;
    case 429:
      return errors.tooManyRequests;
    case 500:
    case 502:
    case 503:
    case 504:
      return errors.serverError;
    default:
      return errors.default;
  }
}

export class ApiError extends Error {
  status: number;
  details?: unknown;

  constructor(message: string, status: number, details?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.details = details;
  }
}

interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let errorMessage = getErrorMessageByStatus(response.status);
    let details: unknown;

    try {
      const errorData = (await response.json()) as {
        title?: string;
        message?: string;
        error?: string;
        detail?: string;
        errors?: { propertyName?: string; errorMessage?: string }[];
      };

      const mappedMessage = errorData.title ? ERROR_CODE_MESSAGES[errorData.title] : undefined;

      if (mappedMessage) {
        errorMessage = mappedMessage;
      } else {
        const validationErrors = errorData.errors;
        if (validationErrors && validationErrors.length > 0) {
          const firstMapped = validationErrors
            .map((e) => (e.errorMessage ? ERROR_CODE_MESSAGES[e.errorMessage] : undefined))
            .find(Boolean);

          errorMessage = firstMapped ?? errorMessage;
        } else {
          const backendMessage = errorData.detail ?? errorData.message ?? errorData.error;
          if (backendMessage && !backendMessage.toLowerCase().includes("exception")) {
            errorMessage = backendMessage;
          }
        }
      }
      details = errorData;
    } catch {
      errorMessage = "An unexpected error occurred";
    }

    throw new ApiError(errorMessage, response.status, details);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

async function request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
  const { body, headers, ...rest } = options;

  const mergedHeaders: HeadersInit = {
    "Content-Type": "application/json",
    ...(headers as Record<string, string> | undefined),
  };

  const config: RequestInit = {
    ...rest,
    headers: mergedHeaders,
    credentials: "include",
  };

  if (body !== undefined) {
    config.body = JSON.stringify(body);
  }

  const url = `${getConfig().apiBaseUrl}${endpoint}`;

  try {
    const response = await fetch(url, config);
    return await handleResponse<T>(response);
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }

    if (error instanceof TypeError && error.message.includes("fetch")) {
      throw new ApiError(messages.errors.network, 0);
    }

    throw new ApiError(messages.errors.default, 0);
  }
}

export const apiClient = {
  get<T>(endpoint: string, options?: RequestOptions): Promise<T> {
    return request<T>(endpoint, { ...options, method: "GET" });
  },
  post<T>(endpoint: string, body?: unknown, options?: RequestOptions): Promise<T> {
    return request<T>(endpoint, { ...options, method: "POST", body });
  },
};
